<template>
  <div class="slice-container" :style="{ width: containerW + 'px', height: containerH + 'px' }">
    <q-icon v-if="blockDatas.associatedResources && blockDatas.style" class="associated-resources-icon2"
      name="new_releases" color="orange" />
    <div
      v-for="(tile, i) in tiles"
      :key="i"
      class="tile"
      :class="{ visible: active }"
      :style="tile"
    />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';

export default {
  name: 'SquareImg',
  props: ['blockDatas'],
  setup(props) {
    const containerW = ref(props.blockDatas.width || 300);
    const containerH = ref(props.blockDatas.height || 300);
    const tiles = ref([]);
    const active = ref(false);

    // Animation duration from Animate.css speed classes
    const animationDuration = ref(0.6);
    const animationDelay = ref(0);

    const speedClass = props.blockDatas.animationSpeed || '';
    if (speedClass.includes('slower')) animationDuration.value = 3;
    else if (speedClass.includes('slow')) animationDuration.value = 2;
    else if (speedClass.includes('fast') && !speedClass.includes('faster')) animationDuration.value = 0.8;
    else if (speedClass.includes('faster')) animationDuration.value = 0.5;

    const delayClass = props.blockDatas.animationDelay || '';
    const delayMatch = delayClass.match(/delay-(\d+)s/);
    if (delayMatch) animationDelay.value = parseInt(delayMatch[1]);

    onMounted(() => {
      const w = containerW.value;
      const h = containerH.value;
      const imgSrc = props.blockDatas.imgSrc;
      if (!imgSrc) return;

      const img = new Image();
      img.onload = () => {
        const natW = img.naturalWidth;
        const natH = img.naturalHeight;

        // Contain mode: fit the full image, no cropping
        const scale = Math.min(w / natW, h / natH);
        const dispW = Math.round(natW * scale);
        const dispH = Math.round(natH * scale);

        // Center the image area in the container
        const offX = Math.round((w - dispW) / 2);
        const offY = Math.round((h - dispH) / 2);

        // Square tiles: divide the shorter displayed side into 3
        const tileSize = Math.ceil(Math.min(dispW, dispH) / 3);
        const cols = Math.ceil(dispW / tileSize);
        const rows = Math.ceil(dispH / tileSize);

        const result = [];
        for (let row = 0; row < rows; row++) {
          for (let col = 0; col < cols; col++) {
            const x = col * tileSize;
            const y = row * tileSize;
            // Edge tiles may be smaller
            const tw = Math.min(tileSize, dispW - x);
            const th = Math.min(tileSize, dispH - y);

            result.push({
              left: (offX + x) + 'px',
              top: (offY + y) + 'px',
              width: tw + 'px',
              height: th + 'px',
              backgroundImage: `url(${imgSrc})`,
              backgroundPosition: `${-x}px ${-y}px`,
              backgroundSize: `${dispW}px ${dispH}px`,
              transitionDelay: `${animationDelay.value + (col + row) * (animationDuration.value / (cols + rows))}s`,
              transitionDuration: `${animationDuration.value}s`,
            });
          }
        }

        tiles.value = result;

        // Double rAF ensures the browser has painted the hidden tiles
        // before we trigger the transition
        requestAnimationFrame(() => {
          requestAnimationFrame(() => { active.value = true; });
        });
      };
      img.src = imgSrc;
    });

    return { containerW, containerH, tiles, active };
  },
};
</script>

<style scoped>
.slice-container {
  position: relative;
  perspective: 600px;
  cursor: pointer;
  overflow: hidden;
}

.tile {
  position: absolute;
  background-repeat: no-repeat;
  opacity: 0;
  transform: rotateY(-30deg) scale(0.8);
  transform-origin: center;
  transition-property: opacity, transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1), cubic-bezier(0.4, 0, 0.2, 1);
}

.tile.visible {
  opacity: 1;
  transform: rotateY(0deg) scale(1);
}

.associated-resources-icon2 {
  position: absolute;
  font-size: 24px;
  z-index: 10;
}
</style>
